import { Select as HeroUISelect } from "@heroui/react";
import { WithId } from "core/types";
import { ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

type LabelPlacement = HeroUISelectProps["labelPlacement"];
type Variant = "bordered" | "flat";
type VariantProp =
  | "outline"
  | "inline"
  | "minimal"
  | "horizontal"
  | "horizontalOutline";
type HeroUISelectProps = React.ComponentProps<typeof HeroUISelect>;
type SelectOverrideVariants = VariantProps<typeof selectOverrides>;
type Size = "md" | "lg" | "sm";

export const selectOverrides = tv({
  compoundVariants: [
    {
      class: {
        trigger: "data-[hover=true]:ring-danger data-[hover=true]:ring-2",
      },
      isInvalid: true,
      variant: "bordered",
    },
    {
      class: {
        trigger: "data-[hover=true]:ring-black data-[hover=true]:ring-2",
      },
      isInvalid: false,
      variant: "bordered",
    },
    // MINIMAL
    {
      class: {
        innerWrapper: "pt-0!",
        label: "group-data-[has-value=true]:hidden!",
        value: "text-sm!",
      },
      labelPlacement: "inside",
      variant: "flat",
    },
  ],
  defaultVariants: {
    color: "primary",
    disableSelectorIconRotation: false,
    fullWidth: true,
    isDisabled: false,
    isMultiline: false,
    labelPlacement: "outside",
    size: "md",
    variant: "bordered",
  },
  slots: {
    base: "",
    endContent:
      // HeroUI: `mb-4` / `mt-4` on outside placements misaligns adornments vs
      // chevron; keep one gutter (`me-1`) + row layout for every variant.
      "mb-0! mt-0! me-1 inline-flex shrink-0 items-center gap-1 self-center leading-none [&_svg]:block [&_svg]:shrink-0 [&_svg]:leading-none",
    errorMessage: "",
    innerWrapper:
      "box-border inline-flex h-fit min-h-4 min-w-0 w-[calc(100%_-_theme(spacing.6))] items-center gap-1.5",
    label: "after:text-gray-900! font-semibold",
    selectorIcon: "text-gray-900 top-1/2! -translate-y-1/2! end-3! w-4! h-4!",
    trigger: "bg-transparent!",
    value: "group-data-[has-value=true]:text-gray-900",
  },
  variants: {
    color: {
      primary: {
        trigger:
          "data-[focus=true]:ring-primary data-[open=true]:ring-primary data-[focus=true]:ring-2 data-[open=true]:ring-2 outline-hidden!",
      },
    },
    isDisabled: {
      true: {
        label: "text-default-disabled!",
        selectorIcon: "text-default-disabled!",
        trigger:
          "border-default-disabled! data-[focus=true]:ring-transparent data-[open=true]:ring-transparent text-default-disabled!",
        value: "text-default-disabled!",
      },
    },
    isInvalid: {
      true: {
        errorMessage: "text-danger-dark!",
        label: "text-gray-900!",
        selectorIcon: "text-danger!",
        trigger:
          "border-danger! data-[focus=true]:border-transparent! data-[open=true]:border-transparent!",
        value: "text-danger!",
      },
    },
    labelPlacement: {
      inside: {
        label: "text-gray-600 text-sm!",
        // Chevron is absolute; value+end sit lower with inside label—pin to
        // bottom padding (trigger heights match Input: h-8 / h-10 / h-12).
        selectorIcon: "top-auto! bottom-1.5! translate-y-0!",
        value: "text-sm!",
      },
      outside: {},
      "outside-left": {
        label: "text-foreground text-sm!",
        trigger: "min-w-[80px]",
      },
      "outside-top": {
        label: "text-gray-900! pb-1.5",
      },
    },
    size: {
      lg: {
        label: "text-medium",
        trigger: "h-12! min-h-12! rounded-large!",
        value: "text-medium",
      },
      md: {
        label: "text-small",
        trigger: "h-10! min-h-10! rounded-medium!",
        value: "text-small",
      },
      sm: {
        label: "text-sm",
        trigger: "h-8! min-h-8! px-2! rounded-small!",
        value: "text-small",
      },
    },
    variant: {
      bordered: {
        trigger: "border-1! border-gray-600 data-[hover=true]:text-gray-900",
      },
      flat: {
        trigger: "border-transparent!",
      },
    },
  },
});

export type SelectProps<T extends WithId = WithId> = Omit<
  HeroUISelectProps,
  | "variant"
  | "color"
  | "radius"
  | "labelPlacement"
  | "size"
  | "children"
  | "classNames"
> &
  Pick<SelectOverrideVariants, "color" | "size"> & {
    "data-testid"?: string;
    items: T[];
    renderItem: (item: T) => ReactNode;
    variant?: VariantProp;
  };

export function mapVariant(variant?: VariantProp): {
  variant: Variant;
  labelPlacement: LabelPlacement;
} {
  if (variant === "outline")
    return {
      labelPlacement: "outside-top",
      variant: "bordered",
    };
  if (variant === "minimal")
    return {
      labelPlacement: "inside",
      variant: "flat",
    };
  if (variant === "inline")
    return {
      labelPlacement: "inside",
      variant: "bordered",
    };
  if (variant === "horizontal")
    return {
      labelPlacement: "outside-left",
      variant: "flat",
    };
  if (variant === "horizontalOutline")
    return {
      labelPlacement: "outside-left",
      variant: "bordered",
    };
  return {
    labelPlacement: "outside",
    variant: "bordered",
  };
}

export function getMergedClassNames({
  isDisabled,
  isInvalid,
  size = "md",
  variantProp,
}: {
  variantProp?: VariantProp;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: Size;
}) {
  const { labelPlacement, variant } = mapVariant(variantProp);
  const overrides = selectOverrides({
    color: "primary",
    isDisabled: isDisabled ?? false,
    isInvalid: isInvalid ?? false,
    labelPlacement,
    size,
    variant,
  });

  return {
    base: [overrides.base()].filter(Boolean).join(" "),
    endContent: [overrides.endContent()].filter(Boolean).join(" "),
    errorMessage: [overrides.errorMessage()].filter(Boolean).join(" "),
    innerWrapper: [overrides.innerWrapper()].filter(Boolean).join(" "),
    label: [overrides.label()].filter(Boolean).join(" "),
    selectorIcon: [overrides.selectorIcon()].filter(Boolean).join(" "),
    trigger: [overrides.trigger()].filter(Boolean).join(" "),
    value: [overrides.value()].filter(Boolean).join(" "),
  };
}
